'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import getLang from 'I18n/utils/getLang';
import H4 from 'UIComponents/elements/headings/H4';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIPaginator from 'UIComponents/paginator/UIPaginator';
import UIEmbeddedComponent from 'ui-addon-iframeable/embed/UIEmbeddedComponent';
import * as RequestStates from 'ecosystem-marketplace-core/constants/RequestState';
import CollectionListing from 'ecosystem-marketplace-core/records/CollectionListing';
import { isFulfilled, isSucceeded } from 'ecosystem-marketplace-core/utils/requestState';
import { usePrevious } from 'ecosystem-marketplace-core/hooks/usePrevious';
import LoadingWrapper from 'ecosystem-marketplace-core/components/LoadingWrapper';
import MetaDescription from 'ecosystem-marketplace-core/components/elements/MetaDescription';
import MetaOpenGraph from 'ecosystem-marketplace-core/components/elements/MetaOpenGraph';
import MetaRobots from 'ecosystem-marketplace-core/components/elements/MetaRobots';
import MetaTwitter from 'ecosystem-marketplace-core/components/elements/MetaTwitter';
import DocumentHead from 'ecosystem-marketplace-core/components/portals/DocumentHead';
import PrerenderReady from 'ecosystem-marketplace-core/components/prerender/PrerenderReady';
import ResultsSection from 'ecosystem-marketplace-core/components/storefront/ResultsSection';
import StorefrontResultsHeader from 'ecosystem-marketplace-core/components/storefront/StorefrontResultsHeader';
import ValidI18nMessage from 'ecosystem-marketplace-core/i18n/components/ValidI18nMessage';
import { setDetailsReferrer } from '../../actions/DetailsActions';
import { fetchCollectionItems } from '../../clients/ecosystemClient';
import { DEFAULT_SEARCH_LENGTH } from '../../constants';
import SearchResult from '../../records/SearchResult';
import { PublicOnly } from '../../selectors/auth';
import { makeGetCollectionListingById, makeGetCollectionListingBySlug } from '../../selectors/collections';
import { getFullHref } from '../../utils/history';
import { validUnescapedText, validText } from 'ecosystem-marketplace-core/i18n/utils';
import { getPathPagination } from '../../utils/urls';
import StorefrontUsageTrackerContext from '../../context/StorefrontUsageTrackerContext';
import CanonicalLink from '../../components/elements/CanonicalLink';
import GenericError from '../../components/error/GenericError';
import ResultsGrid from '../../components/storefront/results/ResultsGrid';
import { useSocialScreenshotUrl } from '../../utils/hooks/useSocialScreenshot';
const EMPTY_COLLECTION_LISTING = new CollectionListing();
const EMPTY_COLLECTION_SEARCH_RESULT = new SearchResult();
const SEARCH_LENGTH = DEFAULT_SEARCH_LENGTH;

const ErrorComponent = () => /*#__PURE__*/_jsx(NavMarker, {
  name: "STOREFRONT_ERROR_LOAD",
  children: /*#__PURE__*/_jsx(GenericError, {})
});

const LoadingComponent = () => /*#__PURE__*/_jsx(UILoadingSpinner, {
  className: "justify-center"
});

const StorefrontCollectionContainer = ({
  history: {
    push
  },
  location: {
    pathname
  },
  match: {
    params: {
      collection: collectionSlug,
      collectionId: previewCollectionId,
      page
    },
    url
  },
  isAdminPreview = false,
  onReady
}) => {
  const reduxDispatch = useDispatch();
  const prevCollectionSlug = usePrevious(collectionSlug);
  const prevPage = usePrevious(page);
  const getCollectionListingBySlug = useMemo(makeGetCollectionListingBySlug, []);
  const getCollectionListingById = useMemo(makeGetCollectionListingById, []);
  const collectionSelector = isAdminPreview ? getCollectionListingById : getCollectionListingBySlug;
  const collectionListing = useSelector(state => collectionSelector(state, {
    slug: collectionSlug,
    collectionId: previewCollectionId
  }) || EMPTY_COLLECTION_LISTING);
  const {
    collectionId,
    id: collectingListingId,
    htmlPageTitle,
    title: collectionListingTitle
  } = collectionListing;
  const collectionI18nKey = useMemo(() => `collection_${collectionId}`, [collectionId]);
  const [requestState, setRequestState] = useState(RequestStates.UNINITIALIZED);
  const requestFulfilled = isFulfilled(requestState);
  const requestSucceeded = isSucceeded(requestState);
  const [searchResult, setSearchResult] = useState(EMPTY_COLLECTION_SEARCH_RESULT);
  const {
    items: searchResultItems,
    total: searchResultTotal
  } = searchResult;
  const pageCount = Math.ceil(searchResultTotal / SEARCH_LENGTH);
  const activePage = page ? parseInt(page, 10) : 1;
  const hasNextPage = activePage < pageCount;
  const hasPrevPage = activePage > 1;
  const offset = (activePage - 1) * SEARCH_LENGTH;
  const searchResultItemsTotal = searchResultItems.size;
  const [pageViewTracked, setPageViewTracked] = useState(false);
  const {
    trackInteraction,
    trackPageView,
    trackResultClick
  } = useContext(StorefrontUsageTrackerContext);
  const handleResultClick = useCallback((result, index) => {
    trackResultClick(result, index, {
      totalResults: searchResultItemsTotal
    });
    reduxDispatch(setDetailsReferrer(`collection::${collectionId}${collectingListingId ? `_${collectingListingId}` : ''}`));
  }, [collectingListingId, collectionId, reduxDispatch, searchResultItemsTotal, trackResultClick]);
  const resultsHeaderResultsCount = useMemo(() => {
    if (!requestSucceeded) {
      return null;
    }

    return {
      start: searchResultTotal ? offset + 1 : 0,
      end: Math.min(offset + searchResultItemsTotal),
      total: Math.max(searchResultTotal, searchResultItemsTotal)
    };
  }, [offset, requestSucceeded, searchResultItemsTotal, searchResultTotal]);
  const canonicalUrlPath = `/${collectionSlug}`;
  const metaDescription = validUnescapedText(`appMarketplace.collections.${collectionI18nKey}.metaDescription`, {
    count: searchResultTotal
  }, collectionListing.metaDescription);
  const noIndexNoFollow = requestSucceeded && searchResultTotal < 3;
  const prerenderStatusCode = requestSucceeded && !searchResultTotal ? 404 : 200;
  const handlePageChange = useCallback(({
    target: {
      value
    }
  }) => {
    push(getPathPagination(pathname, value));
  }, [pathname, push]);
  const paginatorResultsSectionClasses = !(requestSucceeded && pageCount > 1) ? "hidden" : "";
  const language = getLang();
  useEffect(() => {
    setRequestState(RequestStates.PENDING);
    fetchCollectionItems(collectionId, {
      length: SEARCH_LENGTH,
      offset,
      language
    }, isAdminPreview).then(result => {
      setSearchResult(new SearchResult(result));
      setRequestState(RequestStates.SUCCEEDED);
    }).catch(error => {
      setRequestState(RequestStates.FAILED);
      console.error('error fetching collection items:\n', error);
    });
  }, [isAdminPreview, collectionId, offset, language]);
  const documentTitle = useMemo(() => validText(`appMarketplace.collections.${collectionI18nKey}.htmlPageTitle`, {
    count: searchResultTotal
  }, htmlPageTitle), [collectionI18nKey, htmlPageTitle, searchResultTotal]);
  const renderedCollectionTitle = useMemo(() => {
    return searchResultTotal ? /*#__PURE__*/_jsx(ValidI18nMessage, {
      message: `appMarketplace.collections.${collectionI18nKey}.title`,
      defaultValue: collectionListingTitle,
      options: {
        count: searchResultTotal
      }
    }) : null;
  }, [collectionI18nKey, collectionListingTitle, searchResultTotal]);
  useEffect(() => {
    if (requestSucceeded) {
      if (onReady) {
        onReady({
          receiveMessage: () => {}
        });
      }

      document.title = documentTitle;
    }
  }, [onReady, documentTitle, requestSucceeded]);
  useEffect(() => {
    if (requestFulfilled && !pageViewTracked && !isAdminPreview) {
      trackPageView({
        pageNumber: activePage,
        totalResults: searchResultTotal
      });
      setPageViewTracked(true);
    }

    if (requestSucceeded && !searchResultTotal && !isAdminPreview) {
      trackInteraction('no results returned');
    }
  }, [activePage, isAdminPreview, pageViewTracked, requestFulfilled, requestSucceeded, searchResultTotal, trackInteraction, trackPageView]);
  useEffect(() => {
    if (collectionSlug !== prevCollectionSlug || page !== prevPage) {
      setPageViewTracked(false);
    }
  }, [collectionSlug, page, prevCollectionSlug, prevPage]);
  const socialImageUrl = useSocialScreenshotUrl();
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [!isAdminPreview && /*#__PURE__*/_jsx(PublicOnly, {
      children: /*#__PURE__*/_jsxs(DocumentHead, {
        children: [/*#__PURE__*/_jsx(CanonicalLink, {
          to: canonicalUrlPath
        }), hasPrevPage && /*#__PURE__*/_jsx(CanonicalLink, {
          rel: "prev",
          to: getPathPagination(canonicalUrlPath, activePage - 1)
        }), hasNextPage && /*#__PURE__*/_jsx(CanonicalLink, {
          rel: "next",
          to: getPathPagination(canonicalUrlPath, activePage + 1)
        }), /*#__PURE__*/_jsx(MetaDescription, {
          content: metaDescription
        }), /*#__PURE__*/_jsx(MetaRobots, {
          noFollow: noIndexNoFollow,
          noIndex: noIndexNoFollow
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "card"
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "site"
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "image",
          content: socialImageUrl
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "type"
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "title",
          content: documentTitle
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "url",
          content: getFullHref(url)
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "description",
          content: metaDescription
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "image",
          content: socialImageUrl
        }), /*#__PURE__*/_jsx(PrerenderReady, {
          ready: requestFulfilled,
          statusCode: prerenderStatusCode
        })]
      })
    }), /*#__PURE__*/_jsx(LoadingWrapper, {
      ErrorComponent: ErrorComponent,
      LoadingComponent: LoadingComponent,
      requestStatus: requestState,
      children: /*#__PURE__*/_jsxs(NavMarker, {
        name: "STOREFRONT_LOAD",
        children: [searchResultTotal && /*#__PURE__*/_jsx(StorefrontResultsHeader, {
          title: renderedCollectionTitle,
          description: /*#__PURE__*/_jsx(ValidI18nMessage, {
            message: `appMarketplace.collections.${collectionI18nKey}.description`,
            messageComponent: FormattedHTMLMessage,
            defaultValue: collectionListing.description
          }),
          resultsCount: resultsHeaderResultsCount
        }), /*#__PURE__*/_jsx(ResultsSection, {
          children: searchResultTotal > 0 ? /*#__PURE__*/_jsx(ResultsGrid, {
            isAdminPreview: isAdminPreview,
            onResultClick: handleResultClick,
            requestState: requestState,
            results: searchResultItems
          }) : /*#__PURE__*/_jsxs(Fragment, {
            children: [/*#__PURE__*/_jsx(H4, {
              children: /*#__PURE__*/_jsx(FormattedReactMessage, {
                message: "appMarketplace.storefront.results.resultsPage.noResultsEmptyState.filterTitle",
                options: {
                  pageHeading: renderedCollectionTitle
                }
              })
            }), /*#__PURE__*/_jsx("p", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "appMarketplace.storefront.results.resultsPage.noResultsEmptyState.message"
              })
            })]
          })
        }), /*#__PURE__*/_jsx(ResultsSection, {
          className: paginatorResultsSectionClasses,
          children: /*#__PURE__*/_jsx(UIPaginator, {
            onPageChange: handlePageChange,
            page: activePage,
            pageCount: pageCount
          })
        })]
      })
    })]
  });
};

StorefrontCollectionContainer.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.object.isRequired,
    url: PropTypes.string.isRequired
  }).isRequired,
  isAdminPreview: PropTypes.bool,
  onReady: PropTypes.func // From UIEmbeddedComponent HOC, for admin preview route

};
export default UIEmbeddedComponent(StorefrontCollectionContainer, {
  enableTopLevelEmbed: true
});